import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialogModule } from '@angular/material/dialog';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { KnowledgeSourceConfig, KnowledgeSourceConfigCustomDataConfig } from '@vendasta/embeddings';
import { AiKnowledgeService } from '../../ai-knowledge.service';
import { AppsForKnowledgeSourceComponent } from '../../apps-for-knowledge-source/apps-for-knowledge-source.component';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonKnowledgeSourceEditorComponent } from '../common/common-knowledge-source-editor.component';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'ai-knowledge-custom-data',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTabsModule,
    MatSelectModule,
    MatOptionModule,
    MatExpansionModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    RouterModule,
    AppsForKnowledgeSourceComponent,
    GalaxySnackbarModule,
    MatInputModule,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './custom-data.component.html',
  styleUrls: ['./custom-data.component.scss'],
})
export class CustomDataComponent extends CommonKnowledgeSourceEditorComponent {
  private readonly knowledgeService = inject(AiKnowledgeService);
  customDataConfig: KnowledgeSourceConfigCustomDataConfig;
  form: FormGroup | undefined;
  private readonly snackbarService = inject(SnackbarService);

  init(): void {
    this.customDataConfig = this.knowledgeSource?.config?.customDataConfig;
    this.form = new FormGroup({
      name: new FormControl(this.knowledgeSource?.name || '', [Validators.required]),
      content: new FormControl(this.customDataConfig?.text || '', [Validators.required]),
    });
  }

  async submit(): Promise<string> {
    if (!this.isFormValid()) {
      this.snackbarService.openErrorSnack('COMMON.INVALID_FORM');
      return;
    }
    const name = this.form.get('name').value;
    const config = {
      customDataConfig: {
        text: this.form.get('content')?.value,
      },
    } as KnowledgeSourceConfig;
    if (this.knowledgeSource) {
      const source = this.knowledgeSource;
      source.name = name;
      source.config = config;
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(source);
      return source.id;
    } else {
      return await this.knowledgeService.createKnowledgeSource(name, config);
    }
  }

  private isFormValid(): boolean {
    const controls = [this.form.get('name'), this.form.get('content')];
    let valid = true;
    controls.forEach((c) => {
      c?.markAsDirty();
      if (!c?.valid) {
        valid = false;
      }
    });
    return valid;
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }
}
